import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton
} from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  Error as ErrorIcon,
  Description,
  Close
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { apiClient } from '@/services/api';

interface MonitoringIndicatorImportDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  onImportSuccess: () => void;
  apiEndpoint?: string; // 可选的API端点，默认为 monitoring-indicators
}

const MonitoringIndicatorImportDialog: React.FC<MonitoringIndicatorImportDialogProps> = ({ 
  open, 
  onClose, 
  projectId, 
  onImportSuccess,
  apiEndpoint = 'monitoring-indicators' // 默认为事中监控指标
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [importResult, setImportResult] = useState<any>(null);

  // 文件拖拽处理
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const selectedFile = acceptedFiles[0];
      const fileName = selectedFile.name.toLowerCase();
      
      if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.xls')) {
        return;
      }
      
      setFile(selectedFile);
      setImportResult(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: false
  });

  // 处理文件上传和导入
  const handleImport = async () => {
    if (!file) {
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // 模拟上传进度
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      // 调用监控指标导入API
      const response = await apiClient.upload(
        `/projects/${projectId}/${apiEndpoint}/import-excel`,
        file,
        (progress) => {
          setUploadProgress(progress);
        }
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.success) {
        // 导入成功，立即刷新数据并关闭对话框
        onImportSuccess();
        onClose();
        setFile(null);
        setImportResult(null);
        setUploadProgress(0);
      } else {
        throw new Error(response.message || '导入失败');
      }
    } catch (error: any) {
      setImportResult({
        success: false,
        error: error.response?.data?.detail || error.message || '导入失败'
      });
    } finally {
      setIsUploading(false);
    }
  };

  // 重置状态
  const handleClose = () => {
    setFile(null);
    setImportResult(null);
    setUploadProgress(0);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">导入事中监控指标</Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {!file && (
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.300',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover'
              }
            }}
          >
            <input {...getInputProps()} />
            <CloudUpload sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {isDragActive ? '松开文件以上传' : '拖拽文件到此处或点击选择文件'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              支持 .xlsx, .xls 格式
            </Typography>
          </Box>
        )}

        {file && !importResult && (
          <Box sx={{ mt: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Box display="flex" alignItems="center">
                <Description sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="body1" fontWeight="medium">
                  {file.name}
                </Typography>
              </Box>
              <Chip
                label="Excel文档"
                color="primary"
                size="small"
              />
            </Box>
            
            {isUploading && (
              <Box>
                <Typography variant="body2" gutterBottom>
                  正在导入文件...
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="caption" color="text.secondary">
                  {uploadProgress}%
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {importResult && (
          <Box>
            {importResult.success ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                导入成功！共导入 {importResult.data?.importedCount || 0} 个指标
              </Alert>
            ) : (
              <Alert severity="error" sx={{ mb: 2 }}>
                导入失败：{importResult.error}
              </Alert>
            )}

            {/* 导入详情 */}
            {importResult.data && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  导入详情
                </Typography>

                {/* 成功导入的指标 */}
                {importResult.data.importedIndicators && importResult.data.importedIndicators.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      成功导入的指标 ({importResult.data.importedIndicators.length})
                    </Typography>
                    <List dense>
                      {importResult.data.importedIndicators.map((indicator: any, index: number) => (
                        <ListItem key={index} sx={{ pl: 0 }}>
                          <CheckCircle sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                          <ListItemText
                            primary={`${indicator.level3_code || ''} ${indicator.level3_name || ''}`}
                            secondary={`${indicator.level1_name || ''} - ${indicator.level2_name || ''} | 指标值: ${indicator.indicator_value || ''}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {/* 导入错误 */}
                {importResult.data.errors && importResult.data.errors.length > 0 && (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      导入错误 ({importResult.data.errors.length})
                    </Typography>
                    <List dense>
                      {importResult.data.errors.map((error: string, index: number) => (
                        <ListItem key={index} sx={{ pl: 0 }}>
                          <ErrorIcon sx={{ color: 'error.main', mr: 1, fontSize: 20 }} />
                          <ListItemText
                            primary={error}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          {importResult ? '关闭' : '取消'}
        </Button>
        {!importResult && file && (
          <Button
            onClick={handleImport}
            variant="contained"
            disabled={isUploading}
            startIcon={<CloudUpload />}
          >
            {isUploading ? '导入中...' : '开始导入'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MonitoringIndicatorImportDialog;

